'use client';

import {
  acceptAll,
  getConsentPreferences,
  rejectNonEssential,
  saveCustomPreferences,
} from '@lib/analytics/consent';
import { useCallback, useEffect, useState } from 'react';
import { type ConsentCategoryConfig, DEFAULT_PREFERENCES } from './constants';

export type Tab = 'consent' | 'details' | 'about';

export function useModalState(isOpen: boolean, onClose: () => void) {
  const [activeTab, setActiveTab] = useState<Tab>('consent');
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab('consent');
    const stored = getConsentPreferences();
    setPreferences(
      stored
        ? {
            preferences: stored.preferences,
            analytics: stored.analytics,
            advertising: stored.advertising,
          }
        : DEFAULT_PREFERENCES,
    );
  }, [isOpen]);

  const updateCategory = useCallback(
    (category: ConsentCategoryConfig['id'], value: boolean) => {
      setPreferences((prev) => ({ ...prev, [category]: value }));
    },
    [],
  );

  const handleAcceptAll = useCallback(() => {
    acceptAll();
    onClose();
  }, [onClose]);

  const handleNecessaryOnly = useCallback(() => {
    rejectNonEssential();
    onClose();
  }, [onClose]);

  const handleSavePreferences = useCallback(() => {
    saveCustomPreferences(
      preferences.preferences,
      preferences.analytics,
      preferences.advertising,
    );
    onClose();
  }, [preferences, onClose]);

  return {
    activeTab,
    setActiveTab,
    preferences,
    updateCategory,
    handleAcceptAll,
    handleNecessaryOnly,
    handleSavePreferences,
  };
}
